import React, { ReactElement, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Steps, notification, Row, Col } from 'antd'
import { ShareAltOutlined } from '@ant-design/icons'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { EnquiryDetails } from './EnquiryDetails'
import { NotStartedStep } from './NotStartedStep'
import { Proposals } from './Proposals'
import { StepTitle } from './StepTitle'
import { RequestedProposal } from './RequestedProposal'
import { HeavyOutlineButton } from '../../../../Components/UI'
import { ROUTES } from '../../../../Constants'
import { useDateTimeDuration, useTimeSinceNow } from '../../../../Hooks'
import {
  SecondStepQuarterIcon,
  SecondStepThreeQuarterIcon,
  ThirdStepNotStartedIcon,
  ThirdStepIcon
} from './StepIcons'
import './style.less'
import { s } from './style'
import {
  InProgressBookingEventFragment,
  InProgressBookingProposalFragment
} from './InProgressBooking.generated-types'
import { SuppliersWrapper } from '../../../../Components/Suppliers/SuppliersWrapper'
import { CompanyLogo } from './CompanyLogo'

const { Title, Text } = Typography
const { Step } = Steps

interface InProgressEventProps {
  event: InProgressBookingEventFragment
  requestedProposal: InProgressBookingProposalFragment
  refetchEvent: () => void
}

export const InProgressBooking: FC<InProgressEventProps> = ({
  event,
  event: { name, proposals },
  requestedProposal,
  refetchEvent
}): ReactElement => {
  const { user } = useGlobalAuth()
  const { t } = useTranslation()
  const { durationFullDate, durationRange } = useDateTimeDuration(event)
  const {
    medDate: submittedAtDate,
    time: submittedAtTimeString,
    timeSince: timeSinceSubmission
  } = useTimeSinceNow(event.submittedAt)

  // Proposals are not set to rejected when the invitation is declined, so filter them out manually.
  // TODO: remove these when the backend is updated.
  const invitationIdsToInvitations = new Map(
    event.invitations.map((invitation) => [invitation.ID, invitation])
  )
  const filteredProposals = proposals.filter(
    (proposal) => !invitationIdsToInvitations.get(proposal.invitationID)?.declinedAt
  )

  const hasProposals = filteredProposals.filter((p) => p.status === 'SentToGuest').length > 0

  const timeSinceEventSubmission = `${submittedAtDate}, ${submittedAtTimeString} ( ${timeSinceSubmission} )`

  const shareProposalsUrl = `${window.location.href}${ROUTES.ALL_PROPOSALS}/${event.UUID}`

  const awaitingResponsesStepInfo = t(
    'eventHub.inProgress.awaitingResponsesStepInfo.selfServe',
    'We’re matching your enquiry with suitable, available venues - you’ll see their responses here within 24 hours'
  )

  const awaitingBookingStepInfo = t(
    'eventHub.inProgress.awaitingBookingStepInfo.selfServe',
    'Choose a venue, request to book and when everything’s finalised you’ll see your confirmed booking here (we’ll also send you an email)'
  )

  const hasSupplierDeals = event.supplierDeals && event.supplierDeals.length > 0

  const handleShareNotification = async (): Promise<void> => {
    await navigator.clipboard.writeText(shareProposalsUrl)
    notification.success({
      message: (
        <Text style={s.notification.text}>
          {t('eventHub.inProgress.copied', 'Copied to clipboard')}
        </Text>
      ),
      placement: 'topLeft',
      duration: 2,
      style: s.notification.body
    })
  }

  return (
    <div style={s.root}>
      <Row wrap={false} justify='space-between'>
        <Col flex='auto'>
          <Title level={3} style={s.title}>
            {name}
          </Title>
        </Col>
        {Array.isArray(window.env.companyLogo) && window.env.companyLogo.includes(user?.companyID) && (
          <Col flex='none'>
            <CompanyLogo />
          </Col>
        )}
      </Row>
      <Text style={s.dateTime}>
        {durationFullDate}
        {durationRange ? ', ' + durationRange : ''}
      </Text>
      <Steps
        className='in-progress-booking-steps'
        current={requestedProposal ? 2 : 1}
        direction='vertical'>
        <Step
          title={
            <StepTitle
              title={t('eventHub.inProgress.enquirySubmitted', 'Enquiry submitted')}
              timeCompleted={timeSinceEventSubmission}
            />
          }
          description={
            <>
              <EnquiryDetails
                canExpand={hasProposals}
                defaultExpanded={!hasProposals}
                event={event}
              />
              {!hasProposals && hasSupplierDeals ? (
                <SuppliersWrapper supplierDeals={event.supplierDeals} />
              ) : null}
            </>
          }
        />
        <Step
          icon={
            requestedProposal ? null : hasProposals ? (
              <SecondStepThreeQuarterIcon style={s.stepsIcon} />
            ) : (
              <SecondStepQuarterIcon style={s.stepsIcon} />
            )
          }
          title={
            <StepTitle
              title={
                hasProposals
                  ? t('eventHub.inProgress.pickAVenue', 'Time to pick a venue')
                  : t('eventHub.inProgress.workingOnItTitle', "We're working on it")
              }
              extra={
                hasProposals &&
                !requestedProposal && (
                  <HeavyOutlineButton onClick={handleShareNotification} style={s.button}>
                    <ShareAltOutlined />
                    {t('eventHub.inProgress.shareResponses', 'Share responses')}
                  </HeavyOutlineButton>
                )
              }
            />
          }
          description={
            hasProposals ? (
              <>
                <Proposals
                  event={event}
                  defaultExpanded={!requestedProposal}
                  refetchEvent={refetchEvent}
                />
                {hasSupplierDeals ? <SuppliersWrapper supplierDeals={event.supplierDeals} /> : null}
              </>
            ) : (
              <NotStartedStep
                style={{ width: 550, paddingBottom: 39 }}
                content={awaitingResponsesStepInfo}
              />
            )
          }
        />
        <Step
          icon={
            requestedProposal ? (
              <ThirdStepIcon style={s.stepsIcon} />
            ) : (
              <ThirdStepNotStartedIcon style={s.stepsIcon} />
            )
          }
          title={
            <StepTitle
              title={
                requestedProposal
                  ? t('eventHub.inProgress.waitingConfirmation', 'Waiting for confirmation')
                  : t('eventHub.inProgress.confirmBookingTitle', 'Confirm your booking')
              }
            />
          }
          description={
            requestedProposal ? (
              <RequestedProposal event={event} proposal={requestedProposal} />
            ) : (
              <NotStartedStep style={{ width: 700 }} content={awaitingBookingStepInfo} />
            )
          }
        />
      </Steps>
    </div>
  )
}
